import React from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

// Canadian Provinces
const CANADA_PROVINCES = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' },
];

// US States
const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

const AddressForm = ({ address, onChange }) => {
  // Helper function to format to Title Case
  const formatToTitleCase = (str) => {
    if (!str) return '';
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Format Canadian postal code: A1A B2B (uppercase, with space)
  const formatPostalCode = (value, country) => {
    if (!value) return '';
    
    if (country === 'Canada') {
      // Remove all non-alphanumeric characters and convert to uppercase
      const cleaned = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      
      // Format as A1A B2B (with space after 3rd character)
      if (cleaned.length <= 3) {
        return cleaned;
      } else {
        return cleaned.slice(0, 3) + ' ' + cleaned.slice(3, 6);
      }
    } else if (country === 'USA') {
      // US ZIP code: just numbers, max 10 chars (12345 or 12345-6789)
      return value.replace(/[^\d-]/g, '').slice(0, 10);
    }
    
    return value.toUpperCase();
  };

  const updateField = (field, value) => {
    const newAddress = { ...address, [field]: value };
    // Reset province when country changes
    if (field === 'country') {
      newAddress.province = '';
    }
    onChange(newAddress);
  };

  const getProvinceOptions = () => {
    if (address.country === 'Canada') {
      return CANADA_PROVINCES;
    } else if (address.country === 'USA') {
      return US_STATES;
    }
    return [];
  };

  const provinceOptions = getProvinceOptions();
  const showProvinceDropdown = address.country === 'Canada' || address.country === 'USA';
  const provinceLabel = address.country === 'USA' ? 'State' : 'Province';

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Address Line 1</Label>
        <Input
          placeholder="Street address"
          value={address.line1 || ''}
          onChange={(e) => updateField('line1', formatToTitleCase(e.target.value))}
          data-testid="address-line1"
        />
      </div>
      
      <div className="space-y-2">
        <Label>Address Line 2</Label>
        <Input
          placeholder="Apt, suite, unit, etc. (optional)"
          value={address.line2 || ''}
          onChange={(e) => updateField('line2', formatToTitleCase(e.target.value))}
          data-testid="address-line2"
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>City</Label>
          <Input
            placeholder="City"
            value={address.city || ''}
            onChange={(e) => updateField('city', formatToTitleCase(e.target.value))}
            data-testid="address-city"
          />
        </div>
        
        <div className="space-y-2">
          <Label>Country</Label>
          <Select
            value={address.country || ''}
            onValueChange={(v) => updateField('country', v)}
          >
            <SelectTrigger data-testid="address-country">
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Canada">Canada</SelectItem>
              <SelectItem value="USA">USA</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{provinceLabel}</Label>
          {showProvinceDropdown ? (
            <Select
              value={address.province || ''}
              onValueChange={(v) => updateField('province', v)}
            >
              <SelectTrigger data-testid="address-province">
                <SelectValue placeholder={`Select ${provinceLabel.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent>
                {provinceOptions.map((option) => (
                  <SelectItem key={option.code} value={option.code}>
                    {option.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              placeholder={`${provinceLabel} / Region`}
              value={address.province || ''}
              onChange={(e) => updateField('province', e.target.value)}
              data-testid="address-province"
            />
          )}
        </div>
        
        <div className="space-y-2">
          <Label>{address.country === 'USA' ? 'ZIP Code' : 'Postal Code'}</Label>
          <Input
            placeholder={address.country === 'USA' ? '12345' : 'A1A B2B'}
            value={address.postalCode || ''}
            onChange={(e) => updateField('postalCode', formatPostalCode(e.target.value, address.country))}
            maxLength={address.country === 'Canada' ? 7 : 10}
            data-testid="address-postal"
          />
        </div>
      </div>
    </div>
  );
};

export default AddressForm;
